import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  ModalVerificationMethod,
  ModalEmailVerification,
  ModalLineVerification,
  ModalConfirmPassword,
  ModalConnectLineError,
} from ".";
import { VERIFICATION_STEP } from "./constant";
import { OTP_METHOD } from "../../utils/otpMethod";
import { checkExistLineOtpByCode, connectLineOtp } from "../../services/app";
import { encryptClientPassword } from "../../utils/helper";
import * as Notification from "../../Notification";

const VerificationMethodContext = createContext();

export const VerificationMethodProvider = ({
  translateFn,
  user,
  children,
  reloadProfile,
  helpLink,
  setGlobalLoading,
}) => {
  const [isShowModal, setIsShowModal] = useState(false);
  const [step, setStep] = useState(VERIFICATION_STEP.CHOOSE_METHOD);
  const [selectedMethod, setSelectedMethod] = useState(user?.otpMethod ?? null);
  const [lineVerification, setLineVerification] = useState({});
  const [emailVerification, setEmailVerification] = useState({});
  const [verifyOptionParam, setVerifyOptionParam] = useState(null);
  const [hasTwoFa, setHasTwoFa] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingResendOtp, setLoadingResendOtp] = useState(false);
  const [connectLineErrorMessage, setConnectLineErrorMessage] = useState("");
  const T = useMemo(() => {
    if (typeof translateFn === "function") {
      return translateFn;
    }
    return (key) => key;
  }, [translateFn]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [isShowModalConfirmPassword, setIsShowModalConfirmPassword] =
    useState(false);
  useEffect(() => {
    onLineCallback();
  }, [searchParams]);

  const onLineCallback = async () => {
    if (
      searchParams.get("line_code") &&
      searchParams.get("line_state") &&
      searchParams.get("open_confirm_password") === "true"
    ) {
      // set state before connect line
      try {
        const stateBeforeConnectLine = JSON.parse(
          localStorage.getItem("state_before_connect_line")
        );
        if (stateBeforeConnectLine) {
          setSelectedMethod(stateBeforeConnectLine.selectedMethod);
          setEmailVerification(stateBeforeConnectLine.emailVerification || {});
          setLineVerification(stateBeforeConnectLine.lineVerification || {});
          localStorage.removeItem("state_before_connect_line");
        }
      } catch (error) {
        console.error("Error parsing state before connect line:", error);
      }

      // check line account already connected other user
      try {
        const res = await checkExistLineOtpByCode(
          searchParams.get("line_code")
        );
        if (res?.data?.status) {
          const isFriendship = res?.data?.data?.is_friendship ?? false;
          if (isFriendship) {
            setIsShowModalConfirmPassword(true);
            setLineVerification({
              ...lineVerification,
              code: searchParams.get("line_code"),
              state: searchParams.get("line_state"),
              access_token: res?.data?.data?.line_access_token,
            });
          } else {
            setConnectLineErrorMessage("");
            setSearchParams(
              (prev) => {
                const newSearchParams = new URLSearchParams(prev);
                newSearchParams.delete("open_confirm_password");
                newSearchParams.delete("line_code");
                newSearchParams.delete("line_state");
                newSearchParams.set("line_connected", "false");
                return newSearchParams;
              },
              {
                replace: true,
              }
            );
          }
        } else {
          setConnectLineErrorMessage(
            res?.data?.message ??
              T(
                "We couldn't establish a connection with your LINE account at this time."
              )
          );
        }
      } catch (error) {
        console.error("Error checking LINE account:", error);
        setConnectLineErrorMessage(
          error?.response?.data?.message ??
            T("Something went wrong! Please try again later!")
        );
      }
    }
  };

  const [modalConfirmPasswordLoading, setModalConfirmPasswordLoading] =
    useState(false);
  const [modalConfirmPasswordError, setModalConfirmPasswordError] =
    useState("");
  const modalConfirmPasswordOnSubmit = async (password) => {
    const code = lineVerification.code;
    if (code && password) {
      try {
        setModalConfirmPasswordLoading(true);
        const res = await connectLineOtp({
          code,
          password: encryptClientPassword(password),
          line_access_token: lineVerification.access_token,
        });
        if (res?.data?.status) {
          setModalConfirmPasswordError("");
          setIsShowModalConfirmPassword(false);

          // reload user profile
          await reloadProfile();

          // remove search params from URL
          setSearchParams(
            (prev) => {
              const newSearchParams = new URLSearchParams(prev);
              newSearchParams.delete("open_confirm_password");
              newSearchParams.delete("line_code");
              newSearchParams.delete("line_state");
              newSearchParams.set("line_connected", "true");
              return newSearchParams;
            },
            {
              replace: true,
            }
          );
        } else {
          setModalConfirmPasswordError(
            res?.data?.message ??
              "An error occurred while connecting LINE. Please try again."
          );
        }
      } catch (error) {
        setModalConfirmPasswordError(
          error?.response?.data?.message ??
            "An error occurred while connecting LINE. Please try again."
        );
        console.error("LINE callback error:", error);
      } finally {
        setModalConfirmPasswordLoading(false);
      }
    }
  };

  const reset = useCallback(() => {
    setStep(VERIFICATION_STEP.ENTER_OTP);
    setSelectedMethod(user?.otpMethod ?? null);
    setEmailVerification({});
    setLineVerification({});
    setVerifyOptionParam(null);
  }, [user]);

  const verify = useCallback(
    async (options) => {
      setHasTwoFa(
        Boolean(user.is_twofa ?? 0) && !(options?.skipTwoFa ?? false)
      );
      setVerifyOptionParam(options);
      if (options.chooseMethod) {
        setStep(VERIFICATION_STEP.CHOOSE_METHOD);
      } else {
        setStep(VERIFICATION_STEP.ENTER_OTP);
        setSelectedMethod(user?.otpMethod ?? null);
        await sendOtp(user?.otpMethod, options, true);
      }
      setIsShowModal(true);
    },
    [reset, user]
  );

  const renderModalOtpByMethod = () => {
    if (selectedMethod === OTP_METHOD.EMAIL) {
      return (
        <ModalEmailVerification
          loadingResendOtp={loadingResendOtp}
          loading={loading}
          onClose={() => {
            setIsShowModal(false);
            reset();
          }}
          onChangeMethod={() => {
            setStep(VERIFICATION_STEP.CHOOSE_METHOD);
          }}
          onSubmitOtp={async () => {
            setEmailVerification({
              ...emailVerification,
              error: null,
            });
            if (hasTwoFa) {
              if (typeof verifyOptionParam?.verifyOtp === "function") {
                try {
                  setLoading(true);
                  const verifyOtpRes = await verifyOptionParam.verifyOtp(
                    selectedMethod,
                    emailVerification.otp,
                    emailVerification.data
                  );
                  if (verifyOtpRes.success) {
                    setStep(VERIFICATION_STEP.ENTER_TWO_FA);
                    setEmailVerification({
                      ...emailVerification,
                      verifiedOtp: true,
                      otpError: null,
                      data: {
                        ...emailVerification.data,
                        ...verifyOtpRes,
                      },
                    });
                  } else {
                    if (verifyOtpRes.close) {
                      setIsShowModal(false);
                      reset();
                    } else {
                      setEmailVerification({
                        ...emailVerification,
                        otpError:
                          verifyOtpRes?.otpError ??
                          T(
                            "An error occurred while verifying the OTP. Please try again."
                          ),
                      });
                    }
                  }
                } catch (error) {
                  console.error("Error verifying OTP:", error);
                  setEmailVerification({
                    ...emailVerification,
                    error: T(
                      "An error occurred while verifying the OTP. Please try again."
                    ),
                  });
                } finally {
                  setLoading(false);
                }
              }
            } else {
              if (typeof verifyOptionParam.action === "function") {
                try {
                  setLoading(true);
                  const actionRes = await verifyOptionParam.action(
                    selectedMethod,
                    emailVerification.otp,
                    emailVerification.twoFa,
                    emailVerification.data
                  );
                  if (actionRes.success) {
                    setIsShowModal(false);
                    reset();
                  } else {
                    if (actionRes.close) {
                      setIsShowModal(false);
                      reset();
                    } else if (actionRes.otpError) {
                      setEmailVerification({
                        ...emailVerification,
                        otpError: actionRes.otpError,
                      });
                    } else {
                      setIsShowModal(false);
                    }
                  }
                } catch (error) {
                  throw error;
                } finally {
                  setLoading(false);
                }
              }
            }
          }}
          onResend={async () => {
            if (typeof verifyOptionParam?.sendOtp === "function") {
              try {
                setLoadingResendOtp(true);
                const res = await verifyOptionParam.sendOtp(selectedMethod);
                if (res.success) {
                  setEmailVerification({
                    ...emailVerification,
                    duration: 180,
                    otpSentAt: Date.now(),
                    data: {
                      ...emailVerification.data,
                      ...res,
                    },
                    otpError: null,
                  });
                } else {
                  Notification.error(
                    res?.message ??
                      T(
                        "An error occurred while sending the OTP. Please try again."
                      )
                  );
                }
              } catch (error) {
                throw error;
              } finally {
                setLoadingResendOtp(false);
              }
            }
          }}
          onSubmitTwoFa={async () => {
            if (typeof verifyOptionParam.action === "function") {
              try {
                setLoading(true);
                const actionRes = await verifyOptionParam.action(
                  selectedMethod,
                  emailVerification.otp,
                  emailVerification.twoFa,
                  emailVerification.data
                );
                if (actionRes.success) {
                  setIsShowModal(false);
                  reset();
                } else {
                  if (actionRes.close) {
                    setIsShowModal(false);
                    reset();
                  } else if (actionRes.twoFaError) {
                    setEmailVerification({
                      ...emailVerification,
                      twoFaError: actionRes.twoFaError,
                    });
                  } else {
                    setIsShowModal(false);
                  }
                }
              } catch (error) {
                throw error;
              } finally {
                setLoading(false);
              }
            }
          }}
        />
      );
    } else if (selectedMethod === OTP_METHOD.LINE) {
      return (
        <ModalLineVerification
          loading={loading}
          loadingResendOtp={loadingResendOtp}
          onClose={() => {
            setIsShowModal(false);
            reset();
          }}
          onChangeMethod={() => {
            setStep(VERIFICATION_STEP.CHOOSE_METHOD);
          }}
          onSubmitOtp={async () => {
            setLineVerification({
              ...lineVerification,
              error: null,
            });
            if (hasTwoFa) {
              if (typeof verifyOptionParam?.verifyOtp === "function") {
                try {
                  setLoading(true);
                  const verifyOtpRes = await verifyOptionParam.verifyOtp(
                    selectedMethod,
                    lineVerification.otp,
                    lineVerification.data
                  );
                  if (verifyOtpRes.success) {
                    setStep(VERIFICATION_STEP.ENTER_TWO_FA);
                    setLineVerification({
                      ...lineVerification,
                      verifiedOtp: true,
                      otpError: null,
                      data: {
                        ...lineVerification.data,
                        ...verifyOtpRes,
                      },
                    });
                  } else {
                    if (verifyOtpRes.close) {
                      setIsShowModal(false);
                      reset();
                    } else {
                      setLineVerification({
                        ...lineVerification,
                        otpError:
                          verifyOtpRes?.otpError ??
                          T("Invalid verification code"),
                      });
                    }
                  }
                } catch (error) {
                  console.error("Error verifying OTP:", error);
                  setLineVerification({
                    ...lineVerification,
                    otpError: T("Invalid verification code"),
                  });
                } finally {
                  setLoading(false);
                }
              }
            } else {
              if (typeof verifyOptionParam.action === "function") {
                try {
                  setLoading(true);
                  const actionRes = await verifyOptionParam.action(
                    selectedMethod,
                    lineVerification.otp,
                    lineVerification.twoFa,
                    lineVerification.data
                  );
                  if (actionRes.success) {
                    setIsShowModal(false);
                    reset();
                  } else {
                    if (actionRes.close) {
                      setIsShowModal(false);
                      reset();
                    } else if (actionRes.otpError) {
                      setLineVerification({
                        ...lineVerification,
                        otpError: actionRes.otpError,
                      });
                    } else {
                      setIsShowModal(false);
                    }
                  }
                } catch (error) {
                  throw error;
                } finally {
                  setLoading(false);
                }
              }
            }
          }}
          onResend={async () => {
            if (typeof verifyOptionParam?.sendOtp === "function") {
              try {
                setLoadingResendOtp(true);
                const res = await verifyOptionParam.sendOtp(selectedMethod);
                if (res.success) {
                  setLineVerification({
                    ...lineVerification,
                    duration: 180,
                    otpSentAt: Date.now(),
                    data: {
                      ...lineVerification.data,
                      ...res,
                    },
                    otpError: null,
                  });
                } else {
                  Notification.error(
                    res?.message ??
                      T("Something went wrong! Please try again later!")
                  );
                }
              } catch (error) {
                throw error;
              } finally {
                setLoadingResendOtp(false);
              }
            }
          }}
          onSubmitTwoFa={async () => {
            if (typeof verifyOptionParam.action === "function") {
              setLoading(true);
              try {
                const actionRes = await verifyOptionParam.action(
                  selectedMethod,
                  lineVerification.otp,
                  lineVerification.twoFa,
                  lineVerification.data
                );
                if (actionRes.success) {
                  setIsShowModal(false);
                  reset();
                } else {
                  if (actionRes.close) {
                    setIsShowModal(false);
                    reset();
                  } else if (actionRes.twoFaError) {
                    setLineVerification({
                      ...lineVerification,
                      twoFaError: actionRes.twoFaError,
                    });
                  } else {
                    setIsShowModal(false);
                  }
                }
              } catch (error) {
                throw error;
              } finally {
                setLoading(false);
              }
            }
          }}
        />
      );
    }
    return null;
  };

  const checkOtpSent = (method) => {
    if (method === OTP_METHOD.EMAIL && emailVerification.otpSentAt) {
      return true;
    }
    if (method === OTP_METHOD.LINE && lineVerification.otpSentAt) {
      return true;
    }
    return false;
  };

  const sendOtp = async (method, option, isFirstSend = true) => {
    if (typeof option?.sendOtp === "function") {
      try {
        setLoading(true);
        const sendOtpRes = await option.sendOtp(method);
        if (sendOtpRes.success) {
          setStep(VERIFICATION_STEP.ENTER_OTP);
          if (method === OTP_METHOD.EMAIL) {
            setEmailVerification({
              ...emailVerification,
              duration: 180,
              otpSentAt: Date.now(),
              otpError: null,
              otp: null,
              data: {
                ...emailVerification.data,
                ...sendOtpRes,
              },
            });
          }
          if (method === OTP_METHOD.LINE) {
            setLineVerification({
              ...lineVerification,
              duration: 180,
              otpSentAt: Date.now(),
              otpError: null,
              otp: null,
              data: {
                ...lineVerification.data,
                ...sendOtpRes,
              },
            });
          }
        } else {
          if (!isFirstSend) {
            setStep(VERIFICATION_STEP.CHOOSE_METHOD);
          }
          Notification.error(
            sendOtpRes?.message ??
              T("Something went wrong! Please try again later!")
          );
        }
      } catch (error) {
        console.error("Error sending OTP:", error);
        Notification.error(T("Something went wrong! Please try again later!"));
      } finally {
        setLoading(false);
      }
    }
  };

  const modalChooseVerificationMethodOnSubmit = async (method) => {
    setSelectedMethod(method);
    const isOtpSent = checkOtpSent(method);
    if (method === selectedMethod && isOtpSent) {
      if (!verifyOptionParam?.resendEachChooseMethod) {
        setStep(VERIFICATION_STEP.ENTER_OTP);
      } else {
        sendOtp(method, verifyOptionParam, false);
      }
    } else {
      sendOtp(method, verifyOptionParam, false);
    }
  };

  return (
    <VerificationMethodContext.Provider
      value={{
        user,
        T,
        isShowModal,
        setIsShowModal,
        step,
        setStep,
        selectedMethod,
        setSelectedMethod,
        verify,
        verifyOptionParam,
        hasTwoFa,
        loading,
        setLoading,
        emailVerification,
        setEmailVerification,
        lineVerification,
        setLineVerification,
        helpLink,
        setGlobalLoading,
        loadingResendOtp,
      }}
    >
      {children}
      {isShowModal && step === VERIFICATION_STEP.CHOOSE_METHOD && (
        <ModalVerificationMethod
          onClose={() => {
            setStep(VERIFICATION_STEP.ENTER_OTP);
          }}
          loading={loading}
          onSubmit={modalChooseVerificationMethodOnSubmit}
        />
      )}
      {isShowModal &&
        (step === VERIFICATION_STEP.ENTER_OTP ||
          step === VERIFICATION_STEP.ENTER_TWO_FA) &&
        renderModalOtpByMethod()}

      {isShowModalConfirmPassword && (
        <ModalConfirmPassword
          loading={modalConfirmPasswordLoading}
          errorMessage={modalConfirmPasswordError}
          onClose={() => {
            setIsShowModalConfirmPassword(false);
          }}
          onSubmit={modalConfirmPasswordOnSubmit}
        />
      )}
      {Boolean(connectLineErrorMessage) && (
        <ModalConnectLineError
          message={connectLineErrorMessage}
          onClose={() => {
            setConnectLineErrorMessage("");
            setSearchParams(
              (prev) => {
                const newSearchParams = new URLSearchParams(prev);
                newSearchParams.delete("open_confirm_password");
                newSearchParams.delete("line_code");
                newSearchParams.delete("line_state");
                newSearchParams.set("line_connected", "false");
                return newSearchParams;
              },
              {
                replace: true,
              }
            );
          }}
        />
      )}
    </VerificationMethodContext.Provider>
  );
};

export const useVerificationMethodContext = () =>
  useContext(VerificationMethodContext);
